
import {Vue, Component, Watch, Prop} from 'vue-property-decorator';
import {FormWizard, TabContent} from 'vue-form-wizard';
import {SweetModal} from 'sweet-modal-vue';
import stores from "@/stores";
import {WelcomeScreen} from "@/stores/modules/WelcomeScreen";
import {showToaster} from "@/components/DashboardComposer/helpers";
import WelcomeScreenElements from "@/modules/WelcomeScreen/WelcomeScreenElements.vue";
import {RoomType} from "@/stores/RoomType";
import MultiSelect from 'vue-multiselect';
import {DeviceGroups} from "@/stores/DeviceGroup";

@Component({
  components: {
    WelcomeScreenElements,
    SweetModal,
    FormWizard,
    TabContent,
    MultiSelect,
  }
})
export default class CreateWelcomeScreen extends Vue {
  // Data
  welcomeScreen = this.initializeWelcomeScreen();

  wizardKey = 0;

  // Props
  @Prop({default: null})
  item!: IMessage | null;


  allRoomTypes: RoomType[] = [];
  allDeviceGroups: DeviceGroups[] = [];
  timeIntervals: { start: string; end: string }[] = [{ start: '11:00' , end: '12:00'}];

  @Watch('item', {immediate: true})
  onItemChanged() {
    if (this.isEditMode && this.item !== null) {
      this.welcomeScreen = new WelcomeScreen(this.item);
      this.translateSelectedItems();
    }
  }

  @Watch('welcomeScreen.turnOnTvScreen')
  onTurnOnTvScreenChanged() {
    const { turnOnTvScreen, timeIntervals } = this.welcomeScreen;
    if (turnOnTvScreen) {
      if (timeIntervals.length === 0) {
        this.addTimeInterval();
      }
      return;
    }
    this.welcomeScreen.timeIntervals = [];
  }

  selectedRoomTypes: any[] = [];

  updateSelectedRoomTypes(newSelectedRoomTypes: any) {
    this.selectedRoomTypes = this.selectedRoomTypes ?? [];
    this.selectedRoomTypes.splice(0, this.selectedRoomTypes.length, ...newSelectedRoomTypes);
  }

  // Computed
  get isEditMode() {
    return !!this.item;
  }

  mounted() {
    this.initializeData();
  }

  initializeData() {
    this.getAllDeviceGroups();
    this.getAllRoomTypes();
    this.translateSelectedItems();
  }

  initializeWelcomeScreen(): IWelcomeScreen {
    return {
      id: null,
      name: '',
      status: 'ACTIVE',
      active: true,
      elements: [],
      roomTypes: [],
      deviceGroups: [],
      displayOnDevices: false,
      enableLanguageSelector: false,
      showBeforeWelcomeScreen: false,
      turnOnTvScreen: false,
      timeIntervals: [],
    }
  }

  validateForm(formRef: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.$forceUpdate();
      this.$nextTick(() => {
        const form = this.$refs[formRef] as HTMLFormElement;
        form.classList.add('was-validated');
        resolve(form.checkValidity());
      });
    });
  }

  onSubmit() {
    this.validateForm('formStep1').then((isValid) => {
      if (!isValid) {
        return;
      }

      let submitPromise;
      if (this.isEditMode && this.welcomeScreen.id) {
        // Update existing welcome screen

        submitPromise = stores.WelcomeScreen.updateItem(this.welcomeScreen.id, this.welcomeScreen);
      } else {
        // Create new welcome screen
        submitPromise = stores.WelcomeScreen.createNewItem(this.welcomeScreen);
      }

      submitPromise.then((response) => {
        if (response.error) {
          throw new Error(response.error.message);
        }
        showToaster('success', '', Vue.prototype.translate('successfully-updated'));
        const eventToEmit = this.isEditMode ? 'save' : 'created';
        this.$emit(eventToEmit, response.data);
        this.cancel();
      }).catch((error) => {
        showToaster('error', 'ERROR:', error.message || Vue.prototype.translate('error-occurred'));
      });
    });
  }


  saveAsDraft() {
    if (!this.validateForm('formStep1')) {
      return;
    }
    this.welcomeScreen.status = 'DRAFT';
    this.onSubmit();
  }

  publish() {
    if (!this.validateForm('formStep1')) {
      return;
    }
    this.welcomeScreen.status = 'ACTIVE';
    this.onSubmit();
  }

  cancel() {
    (this.$refs.createWizard as any).close();
    this.resetComponent();
  }

  resetComponent() {
    this.welcomeScreen = this.initializeWelcomeScreen();
    this.wizardKey += 1;
  }

  getAllRoomTypes() {
    stores.RoomType.getAllRoomTypes()
      .then((response: any) => {
        this.allRoomTypes = response.data;
        this.allRoomTypes.forEach((item: RoomType) =>
          item.name = Vue.prototype.translate(item.name));
      });
  }

  getAllDeviceGroups() {
    stores.DeviceGroups.getAllDeviceGroups()
      .then((response: any) => {
        this.allDeviceGroups = response.data;
        this.allDeviceGroups.forEach((item: IDeviceGroup) =>
          item.name = Vue.prototype.translate(item.name));
      });
  }


  translateSelectedItems() {
    if (this.welcomeScreen.roomTypes) {
      this.welcomeScreen.roomTypes.forEach(async (item) => {
        item.name = await Vue.prototype.translate(item.name);
      });
    }

    if (this.welcomeScreen.deviceGroups) {
      this.welcomeScreen.deviceGroups.forEach(async (item) => {
        item.name = await Vue.prototype.translate(item.name);
      });
    }
  }

  addTimeInterval() {
    const lastInterval = this.welcomeScreen.timeIntervals[this.welcomeScreen.timeIntervals.length - 1];

    if (lastInterval && this.calculateTimeDifference(lastInterval.end, lastInterval.start) >= 15) {
      this.welcomeScreen.timeIntervals.push({ start: lastInterval.end, end: this.calculateEndTime(lastInterval.end) });
    } else {
      this.welcomeScreen.timeIntervals.push({ start: lastInterval ? lastInterval.end : '11:00', end: this.calculateEndTime(lastInterval ? lastInterval.end : '11:00') });
    }
  }
  removeTimeInterval(index: number) {
    this.welcomeScreen.timeIntervals.splice(index, 1);
  }

  calculateEndTime(selectedTime: string) {
    if (selectedTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const endTime = new Date(0, 0, 0, hours, minutes + 15);
      const formattedHours = endTime.getHours().toString().padStart(2, '0');
      const formattedMinutes = endTime.getMinutes().toString().padStart(2, '0');
      return `${formattedHours}:${formattedMinutes}`;
    }
    return '';
  }

  calculateTimeDifference(endTime: string, startTime: string) {
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    return (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
  }

}
